import { useMemo, useState } from "react";
import { Search, ArrowDownUp, RefreshCw } from "lucide-react";
import { ProductGrid } from "./ProductGrid";
import { flashInfo } from "../lib/flash";

// Shop area: a STICKY category sidebar that stays visible while the product
// catalog scrolls, so you can switch category any time without losing the menu.
export const ShopSection = ({ categories, products, activeCategory, activeLabel, onSelect, onOpen }) => {
  const items = [{ slug: "all", label: "Tutti gli essenziali", tagline: "Tutto" }, ...categories];
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState("relevance");
  const [onlyMonthly, setOnlyMonthly] = useState(false);
  const [onlyFlash, setOnlyFlash] = useState(false);
  const [onlyNew, setOnlyNew] = useState(false);

  const displayed = useMemo(() => {
    let list = [...products];
    if (query.trim()) {
      const q = query.toLowerCase();
      list = list.filter((p) => p.name.toLowerCase().includes(q) || p.description.toLowerCase().includes(q));
    }
    if (onlyMonthly) list = list.filter((p) => p.recurring);
    if (onlyFlash) list = list.filter((p) => flashInfo(p).active);
    if (onlyNew) list = list.filter((p) => p.is_new);
    if (sort === "price_asc") list.sort((a, b) => a.price - b.price);
    else if (sort === "price_desc") list.sort((a, b) => b.price - a.price);
    return list;
  }, [products, query, sort, onlyMonthly, onlyFlash, onlyNew]);

  return (
    <section id="griglia" className="scroll-mt-28 mx-auto max-w-7xl px-6 py-24 md:px-10 md:py-28">
      <div className="mb-10 border-b border-ink/10 pb-6">
        <p className="mb-3 text-xs uppercase tracking-overline text-electric">Il catalogo</p>
        <h2 className="font-serif text-4xl font-light leading-tight text-ink md:text-5xl">
          Scegli una sezione, sfoglia i prodotti
        </h2>
      </div>

      {/* Mobile: horizontal sticky category chips */}
      <div className="sticky top-24 z-30 -mx-6 mb-6 flex gap-2 overflow-x-auto bg-cream/80 px-6 py-3 backdrop-blur-xl md:hidden" data-testid="category-chips-mobile">
        {items.map((c) => (
          <button
            key={c.slug}
            onClick={() => onSelect(c.slug)}
            data-testid={`category-chip-${c.slug}`}
            className={`whitespace-nowrap rounded-full border px-4 py-2 text-sm font-medium transition-colors ${
              activeCategory === c.slug ? "border-ink bg-ink text-cream" : "border-ink/15 text-ink"
            }`}
          >
            {c.label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-10 md:grid-cols-12 md:gap-12">
        {/* Desktop: sticky vertical sidebar */}
        <aside className="hidden md:col-span-3 md:block">
          <div className="sticky top-28" data-testid="category-sidebar">
            <p className="mb-4 text-xs uppercase tracking-overline text-ink-soft">Categorie</p>
            <nav className="flex flex-col gap-1">
              {items.map((c) => {
                const active = activeCategory === c.slug;
                return (
                  <button
                    key={c.slug}
                    onClick={() => onSelect(c.slug)}
                    data-testid={`category-nav-${c.slug}`}
                    className={`group flex items-center justify-between rounded-xl px-4 py-3 text-left transition-colors ${
                      active ? "bg-ink text-cream" : "text-ink hover:bg-ink/5"
                    }`}
                  >
                    <span className="font-serif text-lg leading-tight">{c.label}</span>
                    <span className={`h-1.5 w-1.5 rounded-full transition-colors ${active ? "bg-electric" : "bg-transparent group-hover:bg-ink/20"}`} />
                  </button>
                );
              })}
            </nav>
          </div>
        </aside>

        {/* Catalog */}
        <div className="md:col-span-9">
          {/* Search + sort + monthly toggle */}
          <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center">
            <div className="relative flex-1">
              <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-soft" strokeWidth={1.6} />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Cerca un prodotto…"
                data-testid="product-search"
                className="w-full rounded-full border border-ink/15 bg-panel py-3 pl-11 pr-4 text-sm outline-none focus:border-sage"
              />
              {query.trim().length >= 2 && (
                <div className="absolute left-0 right-0 top-full z-40 mt-2 overflow-hidden rounded-2xl border border-ink/10 bg-panel shadow-xl" data-testid="search-suggestions">
                  {products.filter((p) => p.name.toLowerCase().includes(query.toLowerCase())).slice(0, 5).map((p) => (
                    <button key={p.id} onMouseDown={() => onOpen(p)} data-testid={`suggestion-${p.id}`} className="flex w-full items-center gap-3 px-4 py-2.5 text-left hover:bg-ink/5">
                      <img src={p.image} alt="" className="h-8 w-8 rounded object-cover" />
                      <span className="flex-1 truncate text-sm text-ink">{p.name}</span>
                      <span className="text-xs text-ink-soft">€{p.price.toFixed(2)}</span>
                    </button>
                  ))}
                  {products.filter((p) => p.name.toLowerCase().includes(query.toLowerCase())).length === 0 && (
                    <p className="px-4 py-3 text-sm text-ink-soft">Nessun risultato</p>
                  )}
                </div>
              )}
            </div>
            <div className="relative">
              <ArrowDownUp className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-soft" strokeWidth={1.6} />
              <select
                value={sort}
                onChange={(e) => setSort(e.target.value)}
                data-testid="product-sort"
                className="appearance-none rounded-full border border-ink/15 bg-panel py-3 pl-11 pr-8 text-sm text-ink outline-none focus:border-sage"
              >
                <option value="relevance">Rilevanza</option>
                <option value="price_asc">Prezzo ↑</option>
                <option value="price_desc">Prezzo ↓</option>
              </select>
            </div>
            <button
              onClick={() => setOnlyMonthly((v) => !v)}
              data-testid="only-monthly-toggle"
              className={`flex items-center gap-2 rounded-full border px-4 py-3 text-sm font-medium transition-colors ${
                onlyMonthly ? "border-electric bg-electric text-cream" : "border-ink/15 text-ink hover:bg-ink/5"
              }`}
            >
              <RefreshCw className="h-4 w-4" strokeWidth={1.8} /> Solo mensili
            </button>
            <button
              onClick={() => setOnlyFlash((v) => !v)}
              data-testid="only-flash-toggle"
              className={`flex items-center gap-2 rounded-full border px-4 py-3 text-sm font-medium transition-colors ${
                onlyFlash ? "border-terra bg-terra text-cream" : "border-ink/15 text-ink hover:bg-ink/5"
              }`}
            >
              Solo offerte
            </button>
            <button
              onClick={() => setOnlyNew((v) => !v)}
              data-testid="only-new-toggle"
              className={`flex items-center gap-2 rounded-full border px-4 py-3 text-sm font-medium transition-colors ${
                onlyNew ? "border-lime bg-lime text-cream" : "border-ink/15 text-ink hover:bg-ink/5"
              }`}
            >
              Solo novità
            </button>
          </div>

          <div className="mb-6 flex items-baseline justify-between">
            <h3 className="font-serif text-2xl font-light text-ink md:text-3xl" data-testid="product-grid-title">{activeLabel}</h3>
            <span className="text-sm text-ink-soft" data-testid="product-count">{displayed.length} prodotti</span>
          </div>
          <ProductGrid products={displayed} onOpen={onOpen} />
        </div>
      </div>
    </section>
  );
};
